import { CSSProperties, useEffect, useRef, useState } from 'react';
import { useFileUploader } from '@vivli/shared/infrastructure/hook';
import { IUploader } from '@vivli/shared/features/data-package/infrastructure/interface';
import { Color } from '@vivli/shared/theme';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload } from '@fortawesome/free-solid-svg-icons';
import { useToastApiContext } from '@vivli/core/infrastructure/context';

const EMPTY_FILE_REJECTED_MESSAGE = 'The following empty files are not allowed and will not be uploaded:';

const dragTextStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    backgroundColor: 'white',
    marginBottom: '10px'
};

interface FileUploaderViewProps extends IUploader {
    //computed by UploaderView - gates the drop zone so users without upload rights never see it. (DEV-488)
    showUploader?: boolean;
}

export const FileUploaderView = (props: FileUploaderViewProps) => {
    const {
        uploadUri,
        onFileUploadCompleted,
        onFileUploadError,
        onFileUploadStart,
        onFileUploadProgress,
        onAllFilesCompleted,
        onFileValidation,
        refreshApiToken,
        fileLimitReached,
        showUploader,
        onUploaderInit
    } = props;
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isDragging, setIsDragging] = useState(false);

    const toastService = useToastApiContext();

    const uploaderApi = useFileUploader({
        endpoint: uploadUri,
        maxSimultaneousUploads: 3,
        getAuthToken: refreshApiToken || (() => Promise.resolve('')),
        events: {
            onFileValidation: (fileName) => {
                if (onFileValidation) {
                    return onFileValidation(fileName);
                }
                return true;
            },
            onProgress: (id, fileName, progress) => {
                onFileUploadProgress && onFileUploadProgress(id, fileName, progress);
            },
            onError: (id, fileName, error) => {
                onFileUploadError && onFileUploadError(id, fileName, error.message);
            },
            onComplete: (id, fileName, response) => {
                onFileUploadCompleted && onFileUploadCompleted(id, fileName);
            },
            onStart: (id, fileName, fileSizeKb) => {
                onFileUploadStart && onFileUploadStart(id, fileName, fileSizeKb);
            },
            onAllComplete: (succeeded, failed) => {
                onAllFilesCompleted && onAllFilesCompleted(succeeded as any, failed as any);
            },
            onEmptyFilesRejected: (fileNames: string[]) => {
                const toastDetails = (
                    <div style={{ display: 'grid', gridTemplateRows: 'auto auto', rowGap: '10px' }}>
                        <div>{EMPTY_FILE_REJECTED_MESSAGE}</div>
                        <div>{fileNames.join('\n')}</div>
                    </div>
                );
                toastService.info(toastDetails, {
                    autoClose: 10000,
                    hideProgressBar: false
                });
            }
        }
    });

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = event.target.files;
        if (selectedFiles) {
            uploaderApi.upload(selectedFiles);
        }

        // Clear the input so re-picking the SAME file fires `change` again. A browser only raises
        // the event when the selection actually changes, so without this a file that was cancelled
        // or deleted could not be re-uploaded from the file dialog — the click opened the picker and
        // then nothing happened at all, with no error and no toast, until a page refresh remounted
        // the input. Reset outside the `if` so dismissing the dialog also leaves the input clean.
        // `upload()` takes the File references it needs synchronously, so this is safe here.
        // (DEV-552 / DEV-565)
        event.target.value = '';
    };

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        if (fileLimitReached) return;

        event.preventDefault();
        event.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
        if (fileLimitReached) return;

        event.preventDefault();
        event.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        if (fileLimitReached) return;

        event.preventDefault();
        event.stopPropagation();
        setIsDragging(false);

        const droppedFiles = event.dataTransfer.files;
        if (droppedFiles && droppedFiles.length > 0) {
            uploaderApi.upload(droppedFiles);
        }
    };

    const dragStyle: CSSProperties = {
        backgroundColor: 'transparent',
        textAlign: 'center',
        padding: '20px',
        border: isDragging ? '2px dashed #007bff' : '2px transparent solid',
        cursor: 'pointer',
        margin: '10px 0',
        color: Color.VIVLI_GREY,
        fontSize: '20px'
    };

    useEffect(() => {
        onUploaderInit && onUploaderInit(uploaderApi);
    }, []);

    return (
        <div style={dragStyle} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
            <input type="file" multiple onChange={handleFileChange} style={{ display: 'none' }} ref={fileInputRef} />
            {showUploader && (
                <div style={dragTextStyle} onClick={() => fileInputRef.current?.click()} data-test-id="file-uploader-input">
                    <FontAwesomeIcon icon={faUpload} size="2x" color={Color.VIVLI_GREY} style={{ marginRight: '15px', fontSize: '18px' }} />
                    <div>Click or drag files here to upload.</div>
                </div>
            )}

            {props?.children}
        </div>
    );
};
